/**
 * Domain view of a completed Consultation node's outputJson — the scheduled
 * appointment. `submissionId` is absent on legacy consultations (and on
 * backends predating the submissionId-exposure patch); callers must hide
 * cancel/reschedule actions entirely when it is undefined.
 */
export class AppointmentModel {
  submissionId: string | undefined;
  appointmentTimeUtc: string | undefined;
  address: string | undefined;
  meetingUrl: string | undefined;
  consultationType: string | undefined;

  constructor(init: Partial<AppointmentModel> = {}) {
    this.submissionId = init.submissionId;
    this.appointmentTimeUtc = init.appointmentTimeUtc;
    this.address = init.address;
    this.meetingUrl = init.meetingUrl;
    this.consultationType = init.consultationType;
  }

  /** Human-readable summary: local date/time plus location or meeting link. */
  toDisplayString(): string {
    const when = formatAppointmentTime(this.appointmentTimeUtc);
    const where = this.address ?? this.meetingUrl ?? '';
    if (when !== '' && where !== '') return `${when} — ${where}`;
    return when !== '' ? when : where;
  }
}

/**
 * Casing-tolerant parse of a completed Consultation node's outputJson.
 * Returns undefined for missing/malformed/non-object JSON — never throws.
 */
export function parseConsultationOutput(outputJson?: string | null): AppointmentModel | undefined {
  if (!outputJson) return undefined;

  let parsed: unknown;
  try {
    parsed = JSON.parse(outputJson);
  } catch {
    return undefined;
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return undefined;

  const output = parsed as Record<string, unknown>;
  return new AppointmentModel({
    submissionId: readString(output, 'SubmissionId', 'submissionId'),
    appointmentTimeUtc: readString(output, 'AppointmentTimeUtc', 'appointmentTimeUtc'),
    address: readString(output, 'Address', 'address'),
    meetingUrl: readString(output, 'MeetingUrl', 'meetingUrl'),
    consultationType: readString(output, 'ConsultationType', 'consultationType'),
  });
}

function readString(output: Record<string, unknown>, pascalKey: string, camelKey: string): string | undefined {
  const value = output[pascalKey] ?? output[camelKey];
  return typeof value === 'string' && value !== '' ? value : undefined;
}

function formatAppointmentTime(utcString: string | undefined): string {
  if (!utcString) return '';
  const date = new Date(utcString);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleString(undefined, { dateStyle: 'full', timeStyle: 'short' });
}
